import { InputHTMLAttributes } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaFilePdf } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import toast from "react-hot-toast";
import { Heic, UploadIcon } from "../../assets/svg";

const fileSizeLimit = 10 * 1024 * 1024;
const fileAllowedExtensions = ["png", "jpg", "jpeg", "pdf", "heic"];
const accept =
  "image/jpeg, image/png, application/pdf, image/heic, .heic, .HEIC";

interface InputTextProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  placeholder?: string;
  note?: string;
  requiredMessage?: string;
  isMultiple?: boolean;
  uploadedFiles?: string[];
  onFileSelect: (file: File[]) => void;
  onDeleteImage?: (fileName: string) => void;
  minFile?: number;
}

export default function FileUploader<FieldsType extends FieldValues>({
  control,
  name,
  requiredMessage,
  label,
  shouldUnregister,
  isMultiple,
  required,
  onFileSelect,
  uploadedFiles = [],
  onDeleteImage,
  minFile = 1,
  note = "",
}: InputTextProps<FieldsType>) {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: () =>
          uploadedFiles.length >= minFile ||
          `Please upload atleast ${minFile} files`,
      }}
      shouldUnregister={shouldUnregister}
      render={({ field, fieldState }) => {
        return (
          <div>
            <div className="flex flex-col  items-center my-3 space-y-2">
              {label && (
                <h4 className=" mt-2">
                  {t(label)}
                  {(required || requiredMessage) && (
                    <span className="text-red-600 ml-0.5 text-base">*</span>
                  )}
                </h4>
              )}
              <div className="flex justify-center items-center gap-8 w-full">
                <div className="bg-[#2D6CCA14] w-full border-[#B5C2D1] border-2 border-dashed gap-2 rounded flex justify-center items-center p-3 py-4 relative min-[500px]:max-w-72">
                  <UploadIcon size={36} />
                  <span className="text-[#2D6CCA] font-medium text-center ">
                    {t("Upload you file..")}
                  </span>
                  <input
                    type="file"
                    title=" "
                    multiple={isMultiple}
                    {...field}
                    value={undefined}
                    onChange={(e) => {
                      const files = Array.from(e?.target?.files || []);
                      const validFiles = files.filter(
                        (file) =>
                          file.size <= fileSizeLimit && fileAllowedExtensions
                      );
                      const invalidFiles = files.filter(
                        (file) => file.size > fileSizeLimit
                      );

                      if (invalidFiles.length > 0) {
                        invalidFiles.forEach((file) => {
                          toast.error(
                            `File ${file.name} is too big. Should be less then 20 MB`,
                            {
                              position: "bottom-center",
                            }
                          );
                        });
                      }
                      e.target.value = "";
                      if (validFiles.length > 0) {
                        onFileSelect(validFiles);
                        field.onChange(validFiles);
                      }
                    }}
                    className="absolute inset-0 text-transparent opacity-0 cursor-pointer"
                    accept={accept}
                  />
                </div>
              </div>
              {note && (
                <span className="text-sm text-center text-gray-500">
                  {note}
                </span>
              )}
              {uploadedFiles.length === 0 && fieldState.error ? (
                <p className="text-red-600 font-normal text-sm m-0 p-0  bottom-0 left-0 right-0">
                  {fieldState.error?.message}
                </p>
              ) : null}
            </div>
            <div className={`my-2 space-y-2`}>
              {uploadedFiles.map((url, index) => {
                const fileName = url?.split("/")?.pop();
                const extension = fileName?.substring(
                  fileName.lastIndexOf(".") + 1
                );
                const ISPDF = extension?.toLowerCase() === "pdf";
                const ISHEIC = extension?.toLowerCase() === "heic";
                return (
                  <div key={index} className="flex items-center w-full">
                    <div className="flex items-center  bg-gray-100 rounded-md p-2 w-full overflow-hidden">
                      {ISPDF ? (
                        <FaFilePdf className="size-12 object-cover mr-2 shrink-0" />
                      ) : ISHEIC ? (
                        <Heic
                          width={48}
                          height={48}
                          className="size-12 object-cover mr-2 shrink-0"
                        />
                      ) : (
                        <img
                          src={url}
                          alt=""
                          className="size-12 object-cover rounded mr-2 shrink-0"
                        />
                      )}
                      <span className="overflow-hidden w-full text-ellipsis">
                        {fileName}
                      </span>
                    </div>
                    {onDeleteImage && (
                      <button
                        type="button"
                        className="ml-2"
                        onClick={() => onDeleteImage(fileName)}
                      >
                        <MdDelete
                          size={24}
                          className="text-gray-500 hover:text-gray-800"
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    />
  );
}
